import type { BrandingTemplate, RaceTemplate, DifficultyTemplate } from "@vms/types";
import { useQuery, type QueryState } from "./queries";
import { useInvalidateOn } from "./useInvalidateOn";

/**
 * Org-scoped read-only hooks for the cloud's hub-mirrored template tables.
 * Back the read-only template viewer pages (`pages/org/Templates.tsx`).
 * Authoring lives on each venue's hub UI; these just render what synced up.
 *
 * Each hook subscribes via `useInvalidateOn` so an edit on a connected hub —
 * which syncs to the cloud and triggers an `entity-changed` broadcast — refetches
 * the list without a manual refresh.
 */

function orgEndpoint(base: string, organisationId: string | undefined): string | null {
  return organisationId ? `/api/${base}?organisationId=${organisationId}` : null;
}

export function useBrandingTemplates(
  organisationId: string | undefined,
): QueryState<BrandingTemplate[]> {
  const q = useQuery<BrandingTemplate[]>(orgEndpoint("branding-templates", organisationId));
  useInvalidateOn("branding-template", q.refetch);
  return q;
}

export function useRaceTemplates(
  organisationId: string | undefined,
): QueryState<RaceTemplate[]> {
  const q = useQuery<RaceTemplate[]>(orgEndpoint("race-templates", organisationId));
  useInvalidateOn("race-template", q.refetch);
  return q;
}

export function useDifficultyTemplates(
  organisationId: string | undefined,
): QueryState<DifficultyTemplate[]> {
  const q = useQuery<DifficultyTemplate[]>(orgEndpoint("difficulty-templates", organisationId));
  useInvalidateOn("difficulty-template", q.refetch);
  return q;
}
